/**
 * LayerCrsChip — RFC-112 Phase F CRS alignment status chip
 *
 * Displays CRS alignment information for layers with applied corrections.
 * Shows source CRS, target frame CRS, and scale mode with detailed tooltip.
 */

import { type ReactNode } from 'react';
import { Chip } from '@/design';

export interface LayerCrsChipProps {
  alignment: {
    sourceCrs: string;
    frameCrs: string;
    scaleMode: 'visual' | 'engineering';
    appliedScale: number;
    mercatorScaleAtAnchor: number;
    anchorLatLon: [number, number];
    anchorFrame: [number, number, number];
    correctionLabel: string;
    correctionDetail: string;
    rejected?: { reason: string; code: string };
  } | null;
}

export function LayerCrsChip({ alignment }: LayerCrsChipProps): ReactNode {
  if (!alignment) return null;

  const [lat, lon] = alignment.anchorLatLon;
  const [x, y, z] = alignment.anchorFrame;

  if (alignment.rejected) {
    const tooltip = `${alignment.rejected.reason}\n\nRe-anchor manually...`;
    return (
      <span title={tooltip}>
        <Chip
          label={`CRS · ${alignment.sourceCrs} ⚠`}
          variant="warning"
        />
      </span>
    );
  }

  const tooltip = [
    `Source frame:   ${alignment.sourceCrs}`,
    `Display frame:  ${alignment.frameCrs}`,
    `Anchor:         lat ${lat.toFixed(4)}°, lon ${lon.toFixed(4)}°`,
    `Anchor (frame): (${x.toFixed(1)}, ${y.toFixed(1)}, ${z.toFixed(1)})`,
    `Mercator scale: ${alignment.mercatorScaleAtAnchor.toFixed(4)} at anchor latitude`,
    `Applied scale:  ${alignment.appliedScale.toFixed(4)} (${alignment.scaleMode})`,
  ].join('\n');

  return (
    <span title={tooltip}>
      <Chip
        label={`CRS · ${alignment.sourceCrs} → ${alignment.frameCrs}`}
        variant="neutral"
      />
    </span>
  );
}